import * as React from "react";
import {cn} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import {type ScrollToIdOptions} from "@/ReactInit/Scripts/DashboardPage/types";
import {scrollToId} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardScroll";
import {useEffect} from "react";
import {useIsMobile} from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";

interface ButtonProps {
  onClick: () => void;
  label: string;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "ghost" | "sidebar" | "underline" | "darkplain" | "alert" | "vipps";
  rounded?: "none" | "small" | "medium" | "large" | "full";
  loading?: boolean;
  loadingText?: string;
  icon?: string;
  iconPosition?: "left" | "right";
  iconClass?: string;
  className?: string;
  labelClass?: string;
  alignText?: "center" | "left" | "right";
  width?: "full" | "fit";
  scrollToId?: string;
  scrollOptions?: ScrollToIdOptions;
  scrollWhen?: "before" | "after";
  minLoadingMs?: number;
  customPadding?: string;
  noPadding?: boolean;
  vippsLogoSrc?: string;
  vippsLogoAlt?: string;
  vippsHideLogo?: boolean;
  vippsPrefix?: string;
  vippsLogoClass?: string;
}

interface LinkButtonProps extends Omit<ButtonProps, "onClick"> {
  href: string;
  onClick?: () => void;
}

interface IconButtonProps {
  label: string;
  onClick: () => void;
  icon: string;
  disabled?: boolean;
  variant?: "primary" | "secondary" | "ghost" | "simple" | "sidebar";
  rounded?: "none" | "small" | "medium" | "large" | "full";
  loading?: boolean;
  className?: string;
  size?: "1" | "1.5" | "2" | "2.5" | "3" | "3.5" | "4" | "4.5";
}

function getRoundedClass(rounded?: ButtonProps["rounded"]) {
  switch (rounded) {
    case "none":
      return "!rounded-none";
    case "small":
      return "!rounded-md";
    case "medium":
      return "!rounded-lg";
    case "full":
      return "!rounded-full";
    case "large":
    default:
      return "!rounded-3xl";
  }
}

function getAlignClass(alignText?: ButtonProps["alignText"]) {
  switch (alignText) {
    case "center":
      return "justify-center";
    case "right":
      return "justify-end";
    default:
      return "justify-start";
  }
}

function getVariantClass(variant?: ButtonProps["variant"]) {
  switch (variant) {
    case "primary":
      return "btn-primary";
    case "secondary":
      return "btn-secondary";
    case "ghost":
      return "btn-primary bg-transparent";
    case "sidebar":
      return "btn-sidebar";
    case "underline":
      return "btn-underline";
    case "darkplain":
      return "btn-plain-dark";
    case "alert":
      return "btn-alert";
    case "vipps":
      return "btn-vipps";
    default:
      return "btn-primary";
  }
}

type RenderContentProps =
  Pick<
    ButtonProps,
    | "variant"
    | "label"
    | "labelClass"
    | "loadingText"
    | "icon"
    | "iconPosition"
    | "iconClass"
    | "vippsLogoSrc"
    | "vippsLogoAlt"
    | "vippsHideLogo"
    | "vippsPrefix"
    | "vippsLogoClass"
  > & { visibleLoading: boolean };

const VIPPS_LOGO = "data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjIiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmlld0JveD0iMjIuMDAwMDAwMDAwMDAwMDA0IDIxLjQgMTE5LjQgMzAuNDAwMDAwMDAwMDAwMDA2IiB3aWR0aD0iMTIwIiBoZWlnaHQ9IjMxIj4KCTxzdHlsZT4KCQkuczAgeyBmaWxsOiAjZmZmZmZmIH0gCgk8L3N0eWxlPgoJPGcgaWQ9IkxheWVyIDEiPgoJCTxwYXRoIGNsYXNzPSJzMCIgZD0ibTI4IDIybDUuMSAxNC45IDUtMTQuOWg1LjlsLTguOCAyMi4xaC00LjRsLTguOC0yMi4xeiIvPgoJCTxwYXRoIGNsYXNzPSJzMCIgZD0ibTU3LjMgNDAuNmMzLjcgMCA1LjgtMS44IDcuOC00LjQgMS4xLTEuNCAyLjUtMS43IDMuNS0wLjkgMSAwLjggMS4xIDIuMyAwIDMuNy0yLjkgMy44LTYuNiA2LjEtMTEuMyA2LjEtNS4xIDAtOS42LTIuOC0xMi43LTcuNy0wLjktMS4zLTAuNy0yLjcgMC4zLTMuNCAxLTAuNyAyLjUtMC40IDMuNCAxIDIuMiAzLjMgNS4yIDUuNiA5IDUuNnptNi45LTEyLjNjMCAxLjgtMS40IDMtMyAzLTEuNiAwLTMtMS4yLTMtMyAwLTEuOCAxLjQtMyAzLTMgMS42IDAgMyAxLjMgMyAzeiIvPgoJCTxwYXRoIGNsYXNzPSJzMCIgZD0ibTc4LjMgMjJ2M2MxLjUtMi4xIDMuOC0zLjYgNy4yLTMuNiA0LjMgMCA5LjMgMy42IDkuMyAxMS4zIDAgOC4xLTQuOCAxMi05LjggMTItMi42IDAtNS0xLTYuOC0zLjV2MTAuNmgtNS40di0yOS44em0wIDExYzAgNC41IDIuNiA2LjkgNS41IDYuOSAyLjggMCA1LjYtMi4yIDUuNi02LjkgMC00LjYtMi44LTYuOC01LjYtNi44LTIuOCAwLTUuNSAyLjEtNS41IDYuOHoiLz4KCQk8cGF0aCBjbGFzcz0iczAiIGQ9Im0xMDQuMyAyMnYzYzEuNS0yLjEgMy44LTMuNiA3LjItMy42IDQuMyAwIDkuMyAzLjYgOS4zIDExLjMgMCA4LjEtNC44IDEyLTkuOCAxMi0yLjYgMC01LTEtNi44LTMuNXYxMC42aC01LjR2LTI5Ljh6bTAgMTFjMCA0LjUgMi42IDYuOSA1LjUgNi45IDIuOCAwIDUuNi0yLjIgNS42LTYuOSAwLTQuNi0yLjgtNi44LTUuNi02LjgtMi45IDAtNS41IDIuMS01LjUgNi44eiIvPgoJCTxwYXRoIGNsYXNzPSJzMCIgZD0ibTEzMi4zIDIxLjRjNC41IDAgNy43IDIuMSA5LjEgNy4zbC00LjkgMC44Yy0wLjEtMi42LTEuNy0zLjUtNC4xLTMuNS0xLjggMC0zLjIgMC44LTMuMiAyLjEgMCAxIDAuNyAyIDIuOCAyLjRsMy43IDAuN2MzLjYgMC43IDUuNiAzLjEgNS42IDYuMyAwIDQuOC00LjMgNy4yLTguNCA3LjItNC4zIDAtOS4xLTIuMi05LjgtNy42bDQuOS0wLjhjMC4zIDIuOCAyIDMuOCA0LjggMy44IDIuMSAwIDMuNS0wLjggMy41LTIuMSAwLTEuMi0wLjctMi4xLTMtMi41bC0zLjQtMC42Yy0zLjYtMC43LTUuOC0zLjItNS44LTYuNCAwLjEtNSA0LjYtNy4xIDguMi03LjF6Ii8+Cgk8L2c+Cjwvc3ZnPg=="

function renderButtonContent(props: RenderContentProps) {
  const {
    variant,
    label,
    labelClass,
    visibleLoading,
    loadingText,
    icon,
    iconPosition,
    iconClass,
    vippsLogoSrc,
    vippsLogoAlt = "",
    vippsHideLogo = false,
    vippsPrefix = "Logg inn med",
    vippsLogoClass = "h-4 w-auto mt-1",
  } = props;

  if (visibleLoading) {
    return (
      <>
        <span className={cn(iconClass, "icon-loader animate-spin")} />
        <span className={cn(labelClass, "text-base")}>{loadingText}</span>
      </>
    );
  }

  if (variant === "vipps") {
    const src = vippsLogoSrc ?? VIPPS_LOGO;

    return (
      <span className={cn("flex items-center gap-2", labelClass)}>
        <span aria-hidden="true">{vippsPrefix}</span>
        {!vippsHideLogo && (
          <img
            src={src}
            alt={vippsLogoAlt}
            aria-hidden={vippsLogoAlt === "" ? true : undefined}
            className={vippsLogoClass}
            decoding="async"
          />
        )}
      </span>
    );
  }

  if (!icon) return <span className={labelClass}>{label}</span>;

  const iconElement = <span className={cn(icon, iconClass)} aria-hidden="true"/>;
  return (
    <>
      {iconPosition === "left" && iconElement}
      <span className={cn(labelClass, "text-base")}>{label}</span>
      {iconPosition === "right" && iconElement}
    </>
  );
}

export const DashboardButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      onClick,
      label,
      disabled,
      variant,
      rounded,
      loading,
      minLoadingMs = 750,
      loadingText = "Laster…",
      icon,
      iconPosition = "left",
      iconClass = "",
      className = "",
      labelClass = "",
      alignText = "left",
      width,
      scrollToId: scrollTargetId,
      scrollOptions,
      scrollWhen = "after",
      noPadding = false,
      customPadding,
      vippsLogoSrc,
      vippsLogoAlt = "",
      vippsPrefix = "Logg inn med",
      vippsLogoClass,
    },
    ref
  ) => {
    const [visibleLoading, setVisibleLoading] = React.useState(false);
    const loadingStartRef = React.useRef<number | null>(null);
    const isMobile = useIsMobile();

    const handleClick = () => {
      if (scrollTargetId && scrollWhen === "before") {
        scrollToId(scrollTargetId, scrollOptions);
      }

      onClick();

      if (scrollTargetId && scrollWhen === "after") {
        scrollToId(scrollTargetId, scrollOptions);
      }
    };

    useEffect(() => {
      if (loading) {
        loadingStartRef.current = Date.now();
        setVisibleLoading(true);
        return;
      }

      if (!loading && visibleLoading) {
        const start = loadingStartRef.current;
        const elapsed = start ? Date.now() - start : 0;
        const remaining = Math.max(0, minLoadingMs - elapsed);

        if (remaining === 0) {
          setVisibleLoading(false);
          loadingStartRef.current = null;
        } else {
          const timeout = setTimeout(() => {
            setVisibleLoading(false);
            loadingStartRef.current = null;
          }, remaining);

          return () => clearTimeout(timeout);
        }
      }
    }, [loading, minLoadingMs, visibleLoading]);

    const mobilePadding = customPadding ? customPadding : noPadding ? "!p-0" : isMobile ? "!py-2 !px-4" : "!py-3 !px-6";
    const iconRowClass = visibleLoading || icon || variant === "vipps" ? "flex items-center gap-2" : "";
    const widthClass = width === "full" ? "w-full" : width === "fit" ? "w-fit" : "";

    return (
      <button
        ref={ref}
        type="button"
        className={cn(
          getVariantClass(variant),
          getAlignClass(alignText),
          getRoundedClass(rounded),
          iconRowClass,
          widthClass,
          mobilePadding,
          className
        )}
        onClick={handleClick}
        disabled={disabled || visibleLoading}
        aria-label={variant === "vipps" ? label : undefined}
      >
        {renderButtonContent({
          variant,
          label,
          labelClass,
          visibleLoading,
          loadingText,
          icon,
          iconPosition,
          iconClass,
          vippsLogoSrc,
          vippsLogoAlt,
          vippsPrefix,
          vippsLogoClass,
        })}
      </button>
    );
  }
);

DashboardButton.displayName = "DashboardButton";

export const DashboardLinkButton = React.forwardRef<HTMLAnchorElement, LinkButtonProps>(
  (
    {
      href,
      onClick,
      label,
      disabled,
      variant,
      rounded,
      loading,
      minLoadingMs = 750,
      loadingText = "Laster…",
      icon,
      iconPosition = "left",
      iconClass = "",
      className = "",
      labelClass = "",
      alignText = "left",
      width,
      scrollToId: scrollTargetId,
      scrollOptions,
      scrollWhen = "after",
      noPadding = false,
      customPadding,
      vippsLogoSrc,
      vippsLogoAlt = "",
      vippsHideLogo,
      vippsPrefix = "Logg inn med",
      vippsLogoClass,
    },
    ref
  ) => {
    const [visibleLoading, setVisibleLoading] = React.useState(false);
    const loadingStartRef = React.useRef<number | null>(null);
    const isMobile = useIsMobile();

    useEffect(() => {
      if (loading) {
        loadingStartRef.current = Date.now();
        setVisibleLoading(true);
        return;
      }

      if (!loading && visibleLoading) {
        const start = loadingStartRef.current;
        const elapsed = start ? Date.now() - start : 0;
        const remaining = Math.max(0, minLoadingMs - elapsed);

        if (remaining === 0) {
          setVisibleLoading(false);
          loadingStartRef.current = null;
        } else {
          const timeout = setTimeout(() => {
            setVisibleLoading(false);
            loadingStartRef.current = null;
          }, remaining);

          return () => clearTimeout(timeout);
        }
      }
    }, [loading, minLoadingMs, visibleLoading]);

    const mobilePadding = customPadding ? customPadding : noPadding ? "!p-0" : variant === "vipps" ? "px-14 py-2.5" : isMobile ? "!py-2 !px-4" : "!py-3 !px-6";
    const iconRowClass = visibleLoading || icon || variant === "vipps" ? "flex items-center gap-2" : "";
    const widthClass = width === "full" ? "w-full" : width === "fit" ? "w-fit" : "";

    const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
      if (disabled || visibleLoading) {
        e.preventDefault();
        return;
      }

      if (scrollTargetId && scrollWhen === "before") {
        scrollToId(scrollTargetId, scrollOptions);
      }

      onClick?.();

      if (scrollTargetId && scrollWhen === "after") {
        scrollToId(scrollTargetId, scrollOptions);
      }
    };

    return (
      <a
        ref={ref}
        href={href}
        className={cn(
          getVariantClass(variant),
          getAlignClass(alignText),
          getRoundedClass(rounded),
          iconRowClass,
          widthClass,
          mobilePadding,
          className,
          (disabled || visibleLoading) ? "pointer-events-none opacity-60" : ""
        )}
        onClick={handleClick}
        aria-label={variant === "vipps" ? label : undefined}
        aria-disabled={disabled || visibleLoading ? true : undefined}
      >
        {renderButtonContent({
          variant,
          label,
          labelClass,
          visibleLoading,
          loadingText,
          icon,
          iconPosition,
          iconClass,
          vippsLogoSrc,
          vippsLogoAlt,
          vippsHideLogo,
          vippsPrefix,
          vippsLogoClass,
        })}
      </a>
    );
  }
);

DashboardLinkButton.displayName = "DashboardLinkButton";

export const DashboardIconButton: React.FC<IconButtonProps> = (
  {
    label,
    onClick,
    icon,
    disabled,
    variant,
    rounded = "large",
    loading,
    className = "",
    size = "2",
  }) => {
  const switchRounded = () => {
    switch (rounded) {
      case "none":
        return "!rounded-none";
      case "small":
        return "!rounded-md";
      case "medium":
        return "!rounded-lg";
      case "full":
        return "!rounded-full";
      case "large":
      default:
        return "!rounded-3xl";
    }
  };

  const switchSize = () => {
    switch (size) {
      case "1":
        return "!p-1";
      case "1.5":
        return "!p-1.5";
      case "2":
        return "!p-2";
      case "2.5":
        return "!p-2.5";
      case "3":
        return "!p-3";
      case "3.5":
        return "!p-3.5";
      case "4":
        return "!p-4";
      case "4.5":
        return "!p-4.5";
      default:
        return "!p-2";
    }
  };

  const switchVariant = () => {
    switch (variant) {
      case "primary":
        return "btn-primary";
      case "secondary":
        return "btn-secondary";
      case "ghost":
        return "btn-ghost";
      case "simple":
        return "btn-simple";
      case "sidebar":
        return "btn-sidebar";
      default:
        return "btn-primary";
    }
  };

  return (
    <button
      type="button"
      className={cn(
        switchVariant(),
        switchRounded(),
        switchSize(),
        className,
      )}
      onClick={onClick}
      disabled={disabled || loading}
    >
      <span className="sr-only">{label}</span>
      <div className={icon} aria-hidden="true"/>
    </button>
  );
};
